/**
 * Payment Modal — wraps Stripe Elements for credit card payment.
 *
 * Receives a clientSecret from the backend (PaymentIntent) and renders
 * the Stripe PaymentElement inside a Dialog. On success calls onSuccess().
 */

import { useState } from "react";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import type { StripeElementsOptions } from "@stripe/stripe-js";
import { stripePromise } from "@/lib/stripe";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, Loader2 } from "lucide-react";

interface PaymentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientSecret: string;
  amountCents: number;
  onSuccess: () => void;
}

function CheckoutForm({
  amountCents,
  onSuccess,
  onClose,
}: {
  amountCents: number;
  onSuccess: () => void;
  onClose: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: "if_required",
    });

    if (confirmError) {
      setError(confirmError.message || "Payment failed. Please try again.");
      setProcessing(false);
    } else {
      onSuccess();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: "tabs",
        }}
      />

      {error && (
        <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="flex gap-2 justify-end pt-2">
        <Button type="button" variant="outline" onClick={onClose} disabled={processing}>
          Cancel
        </Button>
        <Button type="submit" disabled={!stripe || !elements || processing}>
          {processing ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            `Pay $${(amountCents / 100).toFixed(2)}`
          )}
        </Button>
      </div>
    </form>
  );
}

export function PaymentModal({
  open,
  onOpenChange,
  clientSecret,
  amountCents,
  onSuccess,
}: PaymentModalProps) {
  if (!stripePromise) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Payment unavailable</DialogTitle>
            <DialogDescription>
              Stripe is not configured. Please contact support.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            Missing Stripe publishable key. Payments cannot be processed.
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const options: StripeElementsOptions = {
    clientSecret,
    appearance: {
      theme: "flat",
      variables: {
        colorPrimary: "#6366f1",
        colorBackground: "#1a1a2e",
        colorText: "#e2e8f0",
        colorDanger: "#ef4444",
        fontFamily: "Inter, system-ui, sans-serif",
        borderRadius: "8px",
        spacingUnit: "4px",
      },
      rules: {
        ".Input": {
          border: "1px solid #334155",
          backgroundColor: "#0f172a",
          color: "#e2e8f0",
        },
        ".Input:focus": {
          borderColor: "#6366f1",
          boxShadow: "0 0 0 1px #6366f1",
        },
        ".Label": {
          color: "#94a3b8",
        },
        ".Tab": {
          border: "1px solid #334155",
          backgroundColor: "#1e293b",
        },
        ".Tab--selected": {
          borderColor: "#6366f1",
          backgroundColor: "#1a1a2e",
        },
      },
    },
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Complete Payment</DialogTitle>
          <DialogDescription>
            Add ${(amountCents / 100).toFixed(2)} in credits to your workspace
          </DialogDescription>
        </DialogHeader>
        <Elements stripe={stripePromise} options={options}>
          <CheckoutForm
            amountCents={amountCents}
            onSuccess={onSuccess}
            onClose={() => onOpenChange(false)}
          />
        </Elements>
      </DialogContent>
    </Dialog>
  );
}
